import { GridColDef, GridColumnGroupingModel } from '@mui/x-data-grid-premium';
import { uniqueId } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ButtonCellComponent,
  EmployeeCellComponent,
  dateCellValueFormatter,
  getFieldNamesFromColumnConfig,
} from '~/components/SolaForceDataGrid';
import { EnumListKey, useAllEnums } from '~/data/enums';
import { WorkingHoursSummaryReportData } from '~/data/reports';
import { useReportNavigateToView } from '~/scenes/PortalModules/Analytics/common';
import { getEnumName } from '~/utils/helpers/enum';

type Row = {
  uniqueId: string;
  empId: number;
  empFirstName: string;
  empLastName: string;
  empNumber: string;
  empUnitName: string;
  empUnitNumber: string;
  country: string;
  legalEntityName: string;
  legalEntityCode: string;
  empPersonnelGroup1: string;
  empPersonnelGroup2: string;
  empPersonnelGroup3: string;
  empPersonnelGroup4: string;
  empPersonnelGroup5: string;
  date: string;
  dailyBalance: string;
  dailyCumulativeBalance: string;
  defaultHours: string;
  correctionTypeHours: string;
  nonWorkingTypeHours: string;
  workingTypeHours: string;
  hasAccount: boolean;
};

type PropsType = {
  data: WorkingHoursSummaryReportData[];
};

export const useWorkingHoursSummaryReport = ({ data }: PropsType) => {
  const { t } = useTranslation();
  const { goToEmployeeBasicDataView } = useReportNavigateToView();
  const { getEnums } = useAllEnums();

  const rows = useMemo(() => {
    return (data || [])
      .filter((entry) => !!entry.fEmpTimeCards?.length)
      .flatMap((row): Row[] => {
        return row.fEmpTimeCards.map(
          (timeCard): Row => ({
            uniqueId: uniqueId(),
            empId: row.fEmpId,
            empFirstName: row.fEmpFirstName,
            empLastName: row.fEmpLastName,
            empNumber: row.fEmpNumber,
            empUnitName: row.fEmpUnitName,
            empUnitNumber: row.fEmpUnitNumber,
            country: row.fEmpOfficeCountry,
            legalEntityName: row.fEmpLegalEntityName,
            legalEntityCode: row.fEmpLegalEntityCode,
            empPersonnelGroup1: row.fEmpPersonnelGroups?.[0] || '',
            empPersonnelGroup2: row.fEmpPersonnelGroups?.[1] || '',
            empPersonnelGroup3: row.fEmpPersonnelGroups?.[2] || '',
            empPersonnelGroup4: row.fEmpPersonnelGroups?.[3] || '',
            empPersonnelGroup5: row.fEmpPersonnelGroups?.[4] || '',
            date: timeCard.entryDate,
            dailyBalance: timeCard.dailyBalance,
            dailyCumulativeBalance: timeCard.cumulativeBalance,
            defaultHours: timeCard.defaultHours,
            correctionTypeHours: timeCard.correctionTypeHours,
            nonWorkingTypeHours: timeCard.nonWorkingTypeHours,
            workingTypeHours: timeCard.workingTypeHours,
            hasAccount: row.fEmpHasAccount,
          }),
        );
      });
  }, [data]);

  const { columnConfigs, columnGroupingModel } = useMemo(() => {
    const countryEnums = getEnums(EnumListKey.COUNTRY);

    const personnelGroupEnumsByGroup = Object.fromEntries(
      [
        EnumListKey.PERSONNEL_GROUP_1,
        EnumListKey.PERSONNEL_GROUP_2,
        EnumListKey.PERSONNEL_GROUP_3,
        EnumListKey.PERSONNEL_GROUP_4,
        EnumListKey.PERSONNEL_GROUP_5,
      ].map((key) => [key, getEnums(key)]),
    );

    const employeeColumnConfigs: GridColDef<Row>[] = [
      {
        field: 'empId',
        headerName: t('laPicture'),
        filterable: false,
        sortable: false,
        disableExport: true,
        renderCell: (params) => (
          <EmployeeCellComponent
            {...params}
            pictureOnly={true}
            onClick={() => goToEmployeeBasicDataView(params.row.empId)}
          />
        ),
        minWidth: 60,
      },
      {
        field: 'empFirstName',
        headerName: t('laFirstName'),
        hideable: false,
        renderCell: (params) => (
          <ButtonCellComponent
            {...params}
            onClick={() => goToEmployeeBasicDataView(params.row.empId)}
          />
        ),
        minWidth: 120,
      },
      {
        field: 'empLastName',
        headerName: t('laLastName'),
        hideable: false,
        renderCell: (params) => (
          <ButtonCellComponent
            {...params}
            onClick={() => goToEmployeeBasicDataView(params.row.empId)}
          />
        ),
        minWidth: 120,
      },
      {
        field: 'empNumber',
        headerName: t('laEmpId'),
        minWidth: 100,
      },
    ];

    const employmentColumnConfigs: GridColDef<Row>[] = [
      {
        field: 'empUnitName',
        headerName: t('laOrgUnit'),
        minWidth: 180,
      },
      {
        field: 'empUnitNumber',
        headerName: t('laUnitNumber'),
        minWidth: 120,
      },
      {
        field: 'country',
        headerName: t('laCountry'),
        valueGetter: (value) => getEnumName(countryEnums, value),
        minWidth: 120,
      },
      {
        field: 'legalEntityCode',
        headerName: t('laLegalEntityCode'),
        minWidth: 140,
      },
      {
        field: 'legalEntityName',
        headerName: t('laLegalEntityName'),
        minWidth: 200,
      },
    ];

    const personnelGroupColumnConfigs: GridColDef<Row>[] = [
      {
        field: 'empPersonnelGroup1',
        headerName: t('fPersonnelGroup1'),
        valueGetter: (value) =>
          getEnumName(
            personnelGroupEnumsByGroup[EnumListKey.PERSONNEL_GROUP_1] ?? [],
            value,
          ),
        minWidth: 150,
      },
      {
        field: 'empPersonnelGroup2',
        headerName: t('fPersonnelGroup2'),
        valueGetter: (value) =>
          getEnumName(
            personnelGroupEnumsByGroup[EnumListKey.PERSONNEL_GROUP_2] ?? [],
            value,
          ),
        minWidth: 150,
      },
      {
        field: 'empPersonnelGroup3',
        headerName: t('fPersonnelGroup3'),
        valueGetter: (value) =>
          getEnumName(
            personnelGroupEnumsByGroup[EnumListKey.PERSONNEL_GROUP_3] ?? [],
            value,
          ),
        minWidth: 150,
      },
      {
        field: 'empPersonnelGroup4',
        headerName: t('fPersonnelGroup4'),
        valueGetter: (value) =>
          getEnumName(
            personnelGroupEnumsByGroup[EnumListKey.PERSONNEL_GROUP_4] ?? [],
            value,
          ),
        minWidth: 150,
      },
      {
        field: 'empPersonnelGroup5',
        headerName: t('fPersonnelGroup5'),
        valueGetter: (value) =>
          getEnumName(
            personnelGroupEnumsByGroup[EnumListKey.PERSONNEL_GROUP_5] ?? [],
            value,
          ),
        minWidth: 150,
      },
    ];

    const workingHoursColumnConfigs: GridColDef<Row>[] = [
      {
        field: 'date',
        headerName: t('laDate'),
        minWidth: 100,
        type: 'date',
        valueFormatter: dateCellValueFormatter,
      },
      {
        field: 'defaultHours',
        headerName: t('laExpectedWorktime'),
        minWidth: 100,
        type: 'number',
      },
      {
        field: 'workingTypeHours',
        headerName: t('laWorkingTypeHours'),
        minWidth: 100,
        type: 'number',
      },
      {
        field: 'nonWorkingTypeHours',
        headerName: t('laNonWorkingTypeHours'),
        minWidth: 100,
        type: 'number',
      },
      {
        field: 'correctionTypeHours',
        headerName: t('laCorrectionTypeHours'),
        minWidth: 100,
        type: 'number',
      },
      {
        field: 'dailyBalance',
        headerName: t('laHourBalanceChange'),
        minWidth: 100,
        type: 'number',
      },
      {
        field: 'dailyCumulativeBalance',
        headerName: t('laHourBalanceInTotal'),
        minWidth: 100,
        type: 'number',
      },
    ];

    const columnConfigs: GridColDef<Row>[] = [
      ...employeeColumnConfigs,
      ...workingHoursColumnConfigs,
      ...employmentColumnConfigs,
      ...personnelGroupColumnConfigs,
    ];

    const columnGroupingModel: GridColumnGroupingModel = [
      {
        groupId: 'employee',
        headerName: t('laEmployee'),
        children: getFieldNamesFromColumnConfig(employeeColumnConfigs),
      },
      {
        groupId: 'workingHours',
        headerName: t('laWorkingHours'),
        children: getFieldNamesFromColumnConfig(workingHoursColumnConfigs),
      },
      {
        groupId: 'employment',
        headerName: t('laEmployment'),
        children: getFieldNamesFromColumnConfig(employmentColumnConfigs),
      },
      {
        groupId: 'personnelGroups',
        headerName: t('laPersonnelGroups'),
        children: getFieldNamesFromColumnConfig(personnelGroupColumnConfigs),
      },
    ];

    return {
      columnConfigs,
      columnGroupingModel,
    };
  }, [t, goToEmployeeBasicDataView, getEnums]);

  return {
    table: {
      columnConfigs,
      rows,
      columnGroupingModel,
    },
  };
};
